import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, configureClient } from "./api";
import { TOKEN_KEY } from "./constants";

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  recoveryEmail?: string | null;
  dateOfBirth?: string | null;
  sex?: string | null;
  tier: string;
  accessEnabled: boolean;
}

interface AuthCtx {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<AuthUser>;
  register: (data: { username: string; firstName: string; lastName: string; dateOfBirth: string; sex?: string; email: string; password: string; turnstileToken?: string }) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem(TOKEN_KEY)));

  const persist = useCallback((t: string | null, u: AuthUser | null) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    setToken(t);
    setUser(u);
  }, []);

  const refresh = useCallback(async () => {
    if (!token) {
      setUser(null);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get<{ user: AuthUser }>("/api/me");
      setUser(r.user);
    } catch {
      persist(null, null);
    } finally {
      setLoading(false);
    }
  }, [token, persist]);

  useEffect(() => {
    configureClient({
      getToken: () => token,
      on401: () => persist(null, null),
    });
    if (token) refresh();
  }, [token, refresh, persist]);

  const login = useCallback(
    async (usernameOrEmail: string, password: string) => {
      const r = await api.post<{ success: boolean; token: string; user: AuthUser }>("/api/auth/login", {
        usernameOrEmail,
        password,
      });
      persist(r.token, r.user);
      return r.user;
    },
    [persist],
  );

  const register = useCallback(
    async (data: { username: string; firstName: string; lastName: string; dateOfBirth: string; sex?: string; email: string; password: string; turnstileToken?: string }) => {
      const r = await api.post<{ success: boolean; token: string; user: AuthUser }>("/api/auth/register", data);
      persist(r.token, r.user);
      return r.user;
    },
    [persist],
  );

  const logout = useCallback(async () => {
    try { await api.post("/api/auth/logout"); } catch { /* noop */ }
    persist(null, null);
  }, [persist]);

  const value = useMemo<AuthCtx>(
    () => ({ user, token, loading, login, register, logout, refresh }),
    [user, token, loading, login, register, logout, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth outside AuthProvider");
  return c;
}
